import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from "@angular/common";
import { Router } from '@angular/router';
import { CoreLanguageService } from '../../services/language.service';
import { WebShopService } from '../../services/webShop.service';
import { BottomSheetHelperService } from '../../services/bottomSheetHelper.service';
import { RemoteGroupOrderServiceTimeExpiredComponent, SecureCheckDialogComponent } from '../../modalDialogs';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CoreLogService } from '../../services/log.service';
import { ChangeDetectionStrategy } from '@angular/compiler/src/core';
import { RemoteOrderGroupServiceSelectorService } from './remoteOrderGroupServiceSelector.service';
import { IGroupManageData, ILocationData, Status } from '../../services/IRemoteGroupOrderService';
import { MatCheckboxChange } from '@angular/material';
import { RemoteGroupOrderService } from '../../services/RemoteGroupOrder.service';

@Component({
  selector: 'remoteOrderGroupServiceSelector',
  templateUrl: './remoteOrderGroupServiceSelector.component.html',
  styleUrls: ['./remoteOrderGroupServiceSelector.component.scss'],
  providers: [RemoteOrderGroupServiceSelectorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("serviceButtonEntering", [
      transition("false => true", [
        query(".animate", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})
export class RemoteOrderGroupServiceSelectorComponent implements OnInit, OnDestroy {
  private _langSubscriber: Subscription;
  public isLoaded: boolean = false;
  public useBrowserHistory: boolean = true;
  public isSmallView: boolean = false;
  public groupManageData: IGroupManageData;
  public stateReady$: Observable<boolean>;
  public userId: number;
  public allMembersAreReady$: Observable<boolean>;
  public locationData$: Observable<ILocationData>;
  public group_basket_price$: Observable<number>;
  public group_basket_count$: Observable<number>;

  public get isGroupCreator(): boolean {
    const me = this;
    return me._remoteOrderGroupServiceSelectorService.isGroupCreator;
  }

  public get isGroupDelivery() {
    const me = this;
    return me._remoteOrderGroupServiceSelectorService.isGroupDelivery;
  }

  constructor(private _remoteOrderGroupServiceSelectorService: RemoteOrderGroupServiceSelectorService
    , private _router: Router
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _location: Location
    , private _webShopService: WebShopService
    , private _remoteGroupOrderService: RemoteGroupOrderService
    , private _coreLanguageService: CoreLanguageService
    , private _bottomSheetHelperService: BottomSheetHelperService
  ) { }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber)
      me._langSubscriber.unsubscribe();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("RemoteOrderGroupServiceSelectorComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.locationData$ = me._remoteOrderGroupServiceSelectorService.locationData$;
    me.groupManageData = me._remoteOrderGroupServiceSelectorService.groupManageData;
    me.stateReady$ = me._remoteOrderGroupServiceSelectorService.stateReady$;
    me.userId = me._remoteOrderGroupServiceSelectorService.userId;
    me.allMembersAreReady$ = me._remoteOrderGroupServiceSelectorService.allMembersAreReady$
    me.group_basket_count$ = me._remoteOrderGroupServiceSelectorService.groupBasketItemsCount$;
    me.group_basket_price$ = me._remoteOrderGroupServiceSelectorService.groupBasketTotalPrice$;
    me._remoteOrderGroupServiceSelectorService.initialize();
  }

  /**
 * Changes the state to ready based on the checkbox event.
 * 
 * @param {MatCheckboxChange} event - The checkbox change event.
 */
  public async changeStateReady(userId: number, event: MatCheckboxChange) {
    const me = this;
    await me._remoteOrderGroupServiceSelectorService.changeStateReady(userId, event.checked);
    me.PushChanges();
  }

  /** Formats the given amount into a currency string.*/
  public formatCurrency(amount: number): string {
    const me = this;
    return me._webShopService.formatCurrency(amount);
  }

  /** Retrieves the group name text with the given group name.*/
  public GetGroupNameText(groupName: string): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Servicepage_Title")
      .replace("{groupname}", "\n" + groupName);
  }

  /** Retrieves the group creator text with the given creator name.*/
  public GetGroupCreatorText(groupCreator: string): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Servicepage_Creator")
      .replace("{creator}", groupCreator);
  }

  /**  Sets the delivery group address with the given address.*/
  public setDeliveryGroupAddress(address: string) {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Delivery_Address_To")
      .replace("{groupaddress}", `<span class="emphasized">${address}</span>`)
  }

  /** Sets the delivery end time with the given service time.*/
  public setDeliveryEndTime(selectedServiceTime: string) {
    const me = this;
    const day = me._displayDay(selectedServiceTime);
    const time = me._displayTime(selectedServiceTime);
    const isdayToday = me._isDayToday(selectedServiceTime);
    if (isdayToday) {
      return me._coreLanguageService.value("phRemoteOrder_Group_Delivery_Time_Today")
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    } else {
      return me._coreLanguageService.value("phRemoteOrder_Group_Delivery_Time")
        .replace("{day}", `<span class="emphasized">${day}</span>`)
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    }
  }

  /** Sets the delivery location name with the given name.*/
  public setDeliveryLocationName(name: string) {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_Group_Delivery_From")
      .replace("{locationname}", `<span class="emphasized">${name}</span>`)
  }

  /** Sets the pickup end time with the given service time. */
  public setPickupEndTime(selectedServiceTime: string) {
    const me = this;
    const day = me._displayDay(selectedServiceTime);
    const time = me._displayTime(selectedServiceTime);
    const isdayToday = me._isDayToday(selectedServiceTime);
    if (isdayToday) {
      return me._coreLanguageService.value("phRemoteOrder_Group_Pickup_Time_Today")
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    } else {
      return me._coreLanguageService.value("phRemoteOrder_Group_Pickup_Time")
        .replace("{day}", `<span class="emphasized">${day}</span>`)
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    }
  }

  /**  Sets the group order end time with the given end time.*/
  public setGroupOrderEndTime(selectedEndTime: string) {
    const me = this;
    const day = me._displayDay(selectedEndTime);
    const time = me._displayTime(selectedEndTime);
    const isdayToday = me._isDayToday(selectedEndTime);
    if (isdayToday) {
      return me._coreLanguageService.value("phRemoteOrder_Group_End_Time_Today")
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    } else {
      return me._coreLanguageService.value("phRemoteOrder_Group_End_Time")
        .replace("{day}", `<span class="emphasized">${day}</span>`)
        .replace("{time}", `<span class="emphasized">${time}</span>`);
    }
  }

  /** Displays the day for the given selected time. */
  private _displayDay(selectedTime: string) {
    const me = this;
    return me._remoteOrderGroupServiceSelectorService.dayDisplayValue(selectedTime)
  }

  /** Displays the time for the given selected time.*/
  private _displayTime(selectedTime: string) {
    const me = this;
    return me._remoteOrderGroupServiceSelectorService.timeDisplayValue(selectedTime)
  }

  /**  Checks if the given day is today */
  private _isDayToday(selectedDay: string) {
    const me = this;
    return me._remoteOrderGroupServiceSelectorService.isDayToday(selectedDay)
  }

  /** Navigates to the group overview page. */
  public navigateToGroupOverview() {
    const me = this;
    me._router.navigate(["/remoteOrderGroupOverview"]);
  }

  /** Navigate to Menugroup */
  public async navigateToMenuGroup() {
    const me = this;
    me._router.navigate(["/menugroup"]);
  }

  public navigateToPayment() {
    const me = this;
    let allMembersAreReady = false;
    let hasItems = false;
    me.group_basket_count$.pipe(take(1)).subscribe(value => hasItems = value > 0).unsubscribe();
    if (hasItems) {
      const expired = me.ServiceTimeExpired();
      me.allMembersAreReady$.pipe(take(1)).subscribe(value => allMembersAreReady = value).unsubscribe();
      if (expired) {
        me.openServiceTimeExpiredDialog(allMembersAreReady);
      } else {
        me.confirmSendOrder(allMembersAreReady);
      }
    }
  }

  private ServiceTimeExpired() {
    const me = this;
    const expired = me._remoteGroupOrderService.ServiceTimeExpired();
    return expired;
  }

  private openServiceTimeExpiredDialog(allMembersAreReady: boolean) {
    const me = this;
    const dialog = me._bottomSheetHelperService.open(RemoteGroupOrderServiceTimeExpiredComponent, {
      data: {
        leadTime: me._remoteGroupOrderService.leadTime,
        selectedServiceTime: me.groupManageData.selectedServiceTime,
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    dialog.afterDismissed().subscribe((dialogResult) => {
      if (dialogResult && dialogResult === "change") {
        me.confirmSendOrder(allMembersAreReady);
        console.log("result", dialogResult);
      }
      me.PushChanges();
    });
  }


  private confirmSendOrder(allMembersAreReady: boolean) {
    const me = this;
    const description = allMembersAreReady ? "phRemoteOrder_SendOrderDialog_Description" : "phRemoteOrder_SendOrderDialog_Description_StateReady";
    const dialog = me._bottomSheetHelperService.open(SecureCheckDialogComponent, {
      data: {
        title: "phRemoteOrder_SendOrderDialog_Title",
        description: description,
        buttonTextAccept: "phRemoteOrder_SendOrderDialog_AcceptBtn",
        buttonTextDeny: "phRemoteOrder_SendOrderDialog_DenyBtn",
        icon: "hypersoft-achtung.svg",
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    dialog.afterDismissed().subscribe(async (result: any) => {
      if (result) {
        await me._remoteGroupOrderService.setGroupPaymentStatus(Status.Finishing);
        me._router.navigate(["/remoteOrderGroupPayment"]);
      } //else nothing
    })
  }

  /** Leave group */
  public async leaveGroup(): Promise<void> {
    const me = this;
    if (me.isGroupCreator) {
      me._remoteOrderGroupServiceSelectorService.openDialogConfirmLeaveGroupCreator(async (stay) => {
        if (stay !== undefined && !stay) {
          await me._remoteOrderGroupServiceSelectorService.leaveGroup();
          me._router.navigate(["/"], { replaceUrl: true });
        }
      });
    } else {
      me._remoteOrderGroupServiceSelectorService.openDialogConfirmLeaveGroupMember(async (stay) => {
        if (stay !== undefined && !stay) {
          await me._remoteOrderGroupServiceSelectorService.leaveGroup();
          me._router.navigate(["/"], { replaceUrl: true });
        }
      });
    }
  }

  /**If small view,navigate back by swiping from left to right.*/
  public swiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  /** Navigate back*/
  public navigateBack() {
    const me = this;
    if (me.useBrowserHistory) {
      me._location.back();
    } else {
      me._router.navigate(["/menuservice"], { replaceUrl: true });
    }
  }

}